import type { TaskOverviewSortingFields } from '@einwert/valuation-api-client'
import { type CustomerAssignedAppraiser as AssignedAppraiser } from '@einwert/valuation-api-client'
import { useGetCurrentCustomer } from 'ClientPortal/src/api/Queries/customers'
import { useGetOrders } from 'ClientPortal/src/api/Queries/orders'
import { useDeleteTask, useGetTasks, useOptimisticPatchTask } from 'ClientPortal/src/api/Queries/tasks'
import { useGetCurrentUser, useGetUsers } from 'ClientPortal/src/api/Queries/users'
import { TaskOverview } from 'ClientPortal/src/pages/Orders/TaskOverview/types'
import type { CustomerUser } from '@einwert/valuation-api-client'
import { assembleAddressFormat, getLocalizedDateString, TaskStatus } from 'ComponentLibrary'
import { LiteralUnion } from 'type-fest'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type UseTaskOverviewPageProps = {
    isGetOrdersEnabled?: boolean
    filtersAndSort: FiltersAndSort
}

export type FiltersAndSort = {
    order?: { id: string; label: string } | null
    status?: LiteralUnion<TaskStatus, string>
    assignee?: CustomerUser | AssignedAppraiser | null
    team?: { id: string; label: string } | null
    portfolio?: { id: string; label: string } | null
    searchString?: string | null
    sortBy: TaskOverviewSortingFields
}

export function useTaskOverviewPage({ filtersAndSort, isGetOrdersEnabled = false }: UseTaskOverviewPageProps) {
    const locale = useCurrentResolvedLocale()
    const { isUserPending, isAppraiser, user } = useGetCurrentUser()

    const { tasks, isTasksPending } = useGetTasks({
        orderId: filtersAndSort.order?.id,
        status: filtersAndSort.status || undefined,
        assignee: filtersAndSort.assignee?.id,
        teamId: filtersAndSort.team?.id,
        portfolioId: filtersAndSort.portfolio?.id,
        searchString: filtersAndSort.searchString || undefined,
        sortBy: filtersAndSort.sortBy,
    })
    const { deleteTask, isDeleteTaskPending } = useDeleteTask()
    const { users, isUsersPending } = useGetUsers()
    const { mutateTask } = useOptimisticPatchTask()
    const { customer, isCustomerInitialLoading } = useGetCurrentCustomer()

    const { orders, isOrdersInitialLoading } = useGetOrders({
        enabled: isGetOrdersEnabled,
    })

    return {
        tasks,
        isTasksPending,
        filtersAndSort,
        deleteTask,
        isDeleteTaskPending,
        isUserPending,
        isAppraiser,
        user,
        users,
        isUsersPending,
        onAssigneeSelect: (userId: string | number | undefined, task: TaskOverview) => {
            mutateTask({
                taskId: task.uuid,
                operations: [
                    {
                        op: 'replace',
                        path: `/assignee`,
                        value: !userId ? null : { id: userId },
                    },
                ],
            })
        },
        updateTaskStatus: (status: string | number, task: TaskOverview, invalidationDelayMilliseconds?: number) => {
            mutateTask({
                taskId: task.uuid,
                operations: [
                    {
                        op: 'replace',
                        path: `/status`,
                        value: status,
                    },
                ],
                invalidationDelayMilliseconds,
            })
        },
        isOrdersInitialLoading,
        orderOptions: orders
            .filter((order) => order.status !== 'temp_hide_in_portal')
            .map((order) => ({
                id: order.uuid,
                label: `${assembleAddressFormat(
                    { street: order.address.street, house_number: order.address.house_number, city: order.address.city },
                    { delimiter: ', ' }
                )} (${getLocalizedDateString(order.created_on, false, locale)}) `,
            })),
        teamOptions: customer?.company_profile?.teams.map((team) => ({ id: team.uuid, label: team.name })) || [],
        isTeamsLoading: isCustomerInitialLoading,
    }
}
